class NotificationPopup extends HTMLElement {
  constructor() {
    super();
    // Pre-binding to ensure 'this' context in event listeners
    this.closePopup = this.closePopup.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleCopyCode = this.handleCopyCode.bind(this);
  }

  connectedCallback() {
    // 1. Better Scoping: Only look inside the parent wrapper or related ID
    // We assume the .newsletter--popup container wraps the <notification-popup>
    // or is a parent sibling in the structure provided in liquid.
    this.popup = this.closest(".newsletter--popup");

    // Safety check: if we can't find the wrapper, stop.
    if (!this.popup) return;

    // Get the section ID from the popup's data attribute
    const sectionId = this.popup.dataset.sectionId;
    this.backdrop = document.querySelector(`[data-backdrop-for="${sectionId}"]`);
    this.closeBtn = this.popup.querySelector(".newsletter__close-btn");
    this.copyBtn = this.popup.querySelector("[data-copy-code]");
    this.form = this.popup.querySelector(".newsletter-form");

    // 2. Logic extraction
    const hasSubscription = this.dataset.hasSubscription === "true";
    const delay = Number(this.dataset.delay) || 0;

    // 3. Editor Logic
    // We parse 'true'/'false' strings from the data attributes
    const isDesignMode = this.dataset.designMode === "true";
    const showInEditor = this.dataset.showInEditor === "true";

    // COOKIE LOGIC
    // Using explicit cookie name to prevent conflicts
    const cookieName = "hideNotificationPopup";
    const isCookieSet = document.cookie.split("; ").some((row) => row.startsWith(`${cookieName}=true`));

    // LOGIC CONTROLLER
    // Stop if user is subscribed or has closed it previously (unless in Design Mode, where we might force it)
    if (hasSubscription || (isCookieSet && !isDesignMode)) return;

    // THEME EDITOR CHECK
    // If we are in the Theme Editor, AND the toggle is OFF, stop here.
    if (isDesignMode && !showInEditor) {
      return;
    }

    // 4. Reveal Popup
    // If delay is 0, run immediately, otherwise wait.
    setTimeout(() => {
      this.openPopup();
    }, delay * 1000);

    // 5. Event Listeners
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", this.closePopup);
    }

    if (this.backdrop) {
      this.backdrop.addEventListener("click", this.handleBackdropClick);
    }

    if (this.copyBtn) {
      this.copyBtn.addEventListener("click", this.handleCopyCode);
    }

    // Handle form submission with AJAX to prevent page refresh
    if (this.form) {
      this.form.addEventListener("submit", this.handleFormSubmit.bind(this));
    }
  }

  openPopup() {
    // Check if the popup handles visibility via the 'hidden' class
    if (this.popup.classList.contains("hidden")) {
      this.popup.classList.remove("hidden");
      this.popup.classList.remove("wt-popup-exit");

      // Show backdrop with animation
      if (this.backdrop) {
        this.backdrop.classList.add("active");
      }

      // Trigger entrance animation
      requestAnimationFrame(() => {
        this.popup.classList.add("wt-popup-fade-in");
      });
    }
  }

  closePopup(event) {
    if (event) event.preventDefault(); // Prevents link href="#" from jumping up page

    // Remove entrance animation and add exit animation
    this.popup.classList.remove("wt-popup-fade-in");
    this.popup.classList.add("wt-popup-exit");

    // Hide backdrop with animation
    if (this.backdrop) {
      this.backdrop.classList.remove("active");
    }

    // Wait for exit animation to complete before hiding
    setTimeout(() => {
      this.popup.classList.add("hidden");
      this.popup.classList.remove("wt-popup-exit");
    }, 300); // Match the animation duration

    // Only set cookie on Live site or if you want to test cookies in Editor (usually better not to set in editor)
    if (this.dataset.designMode !== "true") {
      this.setCookie("hideNotificationPopup", "true", 7);
    }
  }

  handleBackdropClick(event) {
    // Close popup when clicking the backdrop
    if (event.target === this.backdrop) {
      this.closePopup(event);
    }
  }

  handleCopyCode(event) {
    event.preventDefault();

    const codeElement = this.popup.querySelector("[data-discount-code]");
    const copyTextElement = this.copyBtn.querySelector("[data-copy-text]");

    if (!codeElement || !this.copyBtn) return;

    const discountCode = codeElement.textContent.trim();

    // Copy to clipboard
    navigator.clipboard
      .writeText(discountCode)
      .then(() => {
        // Visual feedback
        this.copyBtn.classList.add("copied");
        const originalText = copyTextElement.textContent;
        copyTextElement.textContent = "Copied!";

        // Reset after 2 seconds
        setTimeout(() => {
          this.copyBtn.classList.remove("copied");
          copyTextElement.textContent = originalText;
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy discount code:", err);

        // Fallback: select text for manual copying
        const range = document.createRange();
        range.selectNode(codeElement);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      });
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);
    const submitButton = form.querySelector('button[type="submit"]');
    const fieldset = form.querySelector(".wt-newsletter__fieldset");
    const formStatusDiv = form.querySelector("[data-form-status]");

    // Disable submit button during submission
    if (submitButton) {
      submitButton.disabled = true;
      const buttonText = submitButton.querySelector(".wt-newsletter__send__text");
      if (buttonText) {
        buttonText.dataset.originalText = buttonText.textContent;
        buttonText.textContent = "Submitting...";
      }
    }

    // Submit form via AJAX
    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // Hide the form fieldset
        if (fieldset) {
          fieldset.classList.add("hidden");
        }

        // Show success message
        if (formStatusDiv) {
          formStatusDiv.style.display = "block";
          formStatusDiv.innerHTML = `
            <div class="form__success-field" tabindex="-1" autofocus>
              <p>Thanks for subscribing!</p>
            </div>
          `;

          // Show discount code if configured
          const discountCodeHTML = form.querySelector("[data-discount-template]");
          if (discountCodeHTML) {
            formStatusDiv.insertAdjacentHTML("beforeend", discountCodeHTML.innerHTML);
            // Reinitialize copy button listener
            this.copyBtn = this.popup.querySelector("[data-copy-code]");
            if (this.copyBtn) {
              this.copyBtn.addEventListener("click", this.handleCopyCode);
            }
          }
        }
      })
      .catch((error) => {
        console.error("Form submission error:", error);

        // Show error message
        if (formStatusDiv) {
          formStatusDiv.style.display = "block";
          formStatusDiv.innerHTML = `
            <div class="form__error-field">
              <p>Sorry, there was an error. Please try again.</p>
            </div>
          `;
        }

        // Re-enable submit button
        if (submitButton) {
          submitButton.disabled = false;
          const buttonText = submitButton.querySelector(".wt-newsletter__send__text");
          if (buttonText && buttonText.dataset.originalText) {
            buttonText.textContent = buttonText.dataset.originalText;
          }
        }
      });
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    // SameSite=Lax is better practice for modern browsers
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  disconnectedCallback() {
    // Cleanup event listeners
    if (this.closeBtn) {
      this.closeBtn.removeEventListener("click", this.closePopup);
    }
    if (this.backdrop) {
      this.backdrop.removeEventListener("click", this.handleBackdropClick);
    }
    if (this.copyBtn) {
      this.copyBtn.removeEventListener("click", this.handleCopyCode);
    }
  }
}

customElements.define("notification-popup", NotificationPopup);
